import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Modal,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Button as MuiButton,
  styled,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CategoryIcon from "@mui/icons-material/Category";
import MessageIcon from "@mui/icons-material/Message";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { SubmitButton, ButtonVariantOption } from "@/components/Button";
import { useApiClient } from "@/hooks/useApiClient";
import { submitContact } from "../api/contactAPI";
import { useContactCategories } from "../hooks/useContactCategories";

/**
 * メインダイアログ用のスタイルドコンポーネント
 */
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    minWidth: "320px",
    maxWidth: "700px",
    width: "90vw",
    margin: "16px",
    [theme.breakpoints.up("sm")]: {
      width: "85vw",
      margin: "32px",
    },
    [theme.breakpoints.up("md")]: {
      width: "700px",
    },
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "16px",
  paddingTop: "12px",
  [theme.breakpoints.up("sm")]: {
    padding: "24px",
    paddingTop: "16px",
  },
  [theme.breakpoints.up("md")]: {
    padding: "32px",
    paddingTop: "16px",
  },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
  padding: "8px 0",
  [theme.breakpoints.up("sm")]: {
    marginBottom: "28px",
    padding: "12px 0",
  },
}));

const FormSection = styled(Box)(() => ({
  marginBottom: "24px",
  padding: "24px",
  backgroundColor: "#f8fafc",
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: "12px",
  marginTop: "24px",
  flexDirection: "column",
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    gap: "16px",
    marginTop: "32px",
  },
}));

/**
 * 確認モーダル用のスタイルドコンポーネント
 */
const ConfirmModalContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "600px",
  backgroundColor: "#ffffff",
  borderRadius: "16px",
  padding: "32px",
  maxHeight: "90vh",
  overflow: "auto",
  outline: "none",
  [theme.breakpoints.up("sm")]: {
    width: "80%",
    padding: "40px",
  },
}));

const ConfirmSection = styled(Box)(() => ({
  marginBottom: "24px",
  padding: "20px",
  backgroundColor: "#f8fafc",
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
}));

/**
 * お問い合わせフォームのバリデーションスキーマ
 */
const contactFormSchema = z.object({
  categoryId: z
    .number({ invalid_type_error: "カテゴリを選択してください" })
    .min(1, "カテゴリを選択してください"),
  content: z
    .string()
    .trim()
    .min(10, "10文字以上入力してください")
    .max(1000, "1000文字以内で入力してください"),
});

/**
 * フォームデータ型（未選択状態を含む）
 * defaultValuesでnullを許容するため、categoryIdはnullableにする
 */
interface ContactFormData {
  categoryId: number | null;
  content: string;
}

interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

/**
 * お問い合わせフォームダイアログ
 */
export const ContactFormDialog: React.FC<IProps> = ({ open, setOpen }) => {
  const { apiClient } = useApiClient();

  // カテゴリ一覧
  const {
    categories,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
  } = useContactCategories(open);

  // フォーム状態管理
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // React Hook Form設定
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    getValues,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: "onChange",
    defaultValues: {
      categoryId: null,
      content: "",
    },
  });

  /**
   * フォーム送信処理（確認画面表示）
   */
  const onConfirm = (): void => {
    setShowConfirmModal(true);
  };

  /**
   * 最終送信処理
   */
  const onSubmit = async (formData: ContactFormData): Promise<void> => {
    setIsSubmitting(true);
    try {
      await submitContact(apiClient, {
        categoryId: formData.categoryId as number, // zodのmin(1)で保証済み
        content: formData.content,
      });

      setIsCompleted(true);
      setShowConfirmModal(false);
    } catch (error) {
      Sentry.captureException(error);
      toast.error("お問い合わせの送信に失敗しました");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * ダイアログを閉じる処理
   */
  const handleClose = (): void => {
    if (!isSubmitting) {
      setOpen(false);
      // 状態をリセット
      setTimeout(() => {
        reset();
        setShowConfirmModal(false);
        setIsCompleted(false);
      }, 300);
    }
  };

  /**
   * 確認モーダルを閉じる処理
   */
  const handleConfirmModalClose = (): void => {
    if (!isSubmitting) {
      setShowConfirmModal(false);
    }
  };

  /**
   * 選択中のカテゴリ名を取得
   */
  const getSelectedCategoryName = (): string => {
    const categoryId = getValues("categoryId");
    return categories.find((c) => c.pk === categoryId)?.name ?? "";
  };

  return (
    <>
      {/* メインダイアログ */}
      <StyledDialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleClose();
        }}
      >
        <StyledDialogContent>
          {!isCompleted ? (
            <>
              {/* ヘッダー */}
              <HeaderBox>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    fontWeight: 700,
                    color: "#1f2937",
                    flex: 1,
                    textAlign: "center",
                  }}
                >
                  お問い合わせ
                </Typography>
                <IconButton
                  onClick={handleClose}
                  sx={{
                    position: "absolute",
                    right: { xs: 16, sm: 24, md: 32 },
                    top: { xs: 16, sm: 20 },
                    color: "#6b7280",
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </HeaderBox>

              {/* カテゴリ選択 */}
              <FormSection>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CategoryIcon sx={{ color: "#1976d2", fontSize: "20px" }} />
                  お問い合わせカテゴリ
                </Typography>
                <FormControl fullWidth error={!!errors.categoryId}>
                  <InputLabel id="contact-category-label">
                    カテゴリを選択
                  </InputLabel>
                  <Controller
                    name="categoryId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="contact-category-label"
                        label="カテゴリを選択"
                        disabled={isCategoriesLoading}
                        sx={{
                          backgroundColor: "#ffffff",
                          borderRadius: "8px",
                        }}
                      >
                        {isCategoriesLoading ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            読み込み中...
                          </MenuItem>
                        ) : isCategoriesError ? (
                          <MenuItem disabled>
                            カテゴリの取得に失敗しました
                          </MenuItem>
                        ) : (
                          categories.map((category) => (
                            <MenuItem key={category.pk} value={category.pk}>
                              {category.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    )}
                  />
                  {errors.categoryId && (
                    <FormHelperText>{errors.categoryId.message}</FormHelperText>
                  )}
                </FormControl>
              </FormSection>

              {/* お問い合わせ内容 */}
              <FormSection>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <MessageIcon sx={{ color: "#1976d2", fontSize: "20px" }} />
                  お問い合わせ内容
                </Typography>
                <FormControl fullWidth error={!!errors.content}>
                  <Controller
                    name="content"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        multiline
                        rows={8}
                        placeholder="お問い合わせ内容を入力してください（10文字以上1000文字以内）"
                        error={!!errors.content}
                        helperText={errors.content?.message}
                        inputProps={{ maxLength: 1000 }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#ffffff",
                            borderRadius: "8px",
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              </FormSection>

              {/* ボタン */}
              <ButtonContainer>
                <MuiButton
                  variant="outlined"
                  onClick={handleClose}
                  sx={{
                    minWidth: { xs: "100%", sm: "140px" },
                    height: "48px",
                    fontSize: "0.95rem",
                    border: "2px solid #e2e8f0",
                    color: "#475569",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                      borderColor: "#cbd5e1",
                    },
                  }}
                >
                  キャンセル
                </MuiButton>
                <MuiButton
                  variant="contained"
                  onClick={() => {
                    void handleSubmit(onConfirm)();
                  }}
                  disabled={!isValid}
                  sx={{
                    minWidth: { xs: "100%", sm: "140px" },
                    height: "48px",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                >
                  確認画面へ
                </MuiButton>
              </ButtonContainer>
            </>
          ) : (
            <>
              {/* 完了画面 */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 80, color: "#10b981", mb: 3 }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "#1f2937",
                    mb: 2,
                    textAlign: "center",
                  }}
                >
                  お問い合わせを送信しました
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#6b7280", textAlign: "center" }}
                >
                  担当者より順次ご連絡いたします。
                </Typography>
              </Box>
              <ButtonContainer>
                <MuiButton
                  variant="contained"
                  onClick={handleClose}
                  sx={{
                    minWidth: { xs: "100%", sm: "160px" },
                    height: "48px",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                >
                  閉じる
                </MuiButton>
              </ButtonContainer>
            </>
          )}
        </StyledDialogContent>
      </StyledDialog>

      {/* 確認モーダル */}
      <Modal
        open={showConfirmModal}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleConfirmModalClose();
        }}
        aria-labelledby="confirm-modal-title"
      >
        <ConfirmModalContainer>
          <Typography
            id="confirm-modal-title"
            variant="h5"
            component="h2"
            sx={{
              fontSize: "1.5rem",
              fontWeight: 700,
              color: "#1f2937",
              marginBottom: "24px",
              textAlign: "center",
            }}
          >
            お問い合わせ内容の確認
          </Typography>

          {/* カテゴリ */}
          <ConfirmSection>
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "12px",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CategoryIcon sx={{ color: "#1976d2", fontSize: "20px" }} />
              お問い合わせカテゴリ
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#1f2937",
                fontSize: "0.95rem",
                lineHeight: 1.5,
              }}
            >
              {getSelectedCategoryName()}
            </Typography>
          </ConfirmSection>

          {/* お問い合わせ内容 */}
          <ConfirmSection>
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "12px",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <MessageIcon sx={{ color: "#1976d2", fontSize: "20px" }} />
              お問い合わせ内容
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#1f2937",
                fontSize: "0.95rem",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                padding: "16px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
              }}
            >
              {getValues("content")}
            </Typography>
          </ConfirmSection>

          <Typography
            variant="body2"
            sx={{
              color: "#6b7280",
              textAlign: "center",
              marginBottom: "24px",
            }}
          >
            上記の内容でお問い合わせを送信いたします。よろしいでしょうか？
          </Typography>

          {/* ボタン */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <MuiButton
              variant="outlined"
              onClick={handleConfirmModalClose}
              disabled={isSubmitting}
              sx={{
                minWidth: { xs: "100%", sm: "140px" },
                height: "48px",
                fontSize: "0.95rem",
                border: "2px solid #e2e8f0",
                color: "#475569",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#f8fafc",
                  borderColor: "#cbd5e1",
                },
              }}
            >
              戻る
            </MuiButton>
            <SubmitButton
              timeout={120000}
              variant={ButtonVariantOption.Contained}
              onClick={(_event, cancelLoading) => {
                void handleSubmit(onSubmit)().finally(() => {
                  cancelLoading();
                });
              }}
              label={isSubmitting ? "送信中..." : "送信する"}
              sx={{
                minWidth: { xs: "100%", sm: "140px" },
                height: "48px",
                fontSize: "0.95rem",
                fontWeight: 600,
                textTransform: "none",
              }}
            />
          </Box>
        </ConfirmModalContainer>
      </Modal>
    </>
  );
};
